import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserPhoneNumberConfirmRequest } from '@kcftech/prometheustypescriptapiclient';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { Menu } from 'components/top_bar/styled';
import { ChangePasswordPopupModal } from 'components/user_menu/ChangePasswordPopupModal';
import { PhoneNumberConfirmModal } from 'components/user_menu/PhoneNumberConfirmModal';
import { UserMenu } from 'components/user_menu/UserMenu';
import { UserProfileModal } from 'components/user_menu/UserProfileModal';
import { sdClient } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { currentUserInfoQueryKey } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { usePopup } from 'custom_hooks/usePopup';
import { useRef, useState } from 'react';
import { MenuButton } from 'styles/theme_styles/button';
import { queryClient } from 'utils/query';
import { updateCurrentUser } from 'utils/user';

export function UserPopup() {
    const phonePopupRef = useRef<HTMLDivElement>(null);
    const { isOpen, toggleOpen, close, triggerRef, popupRef } = usePopup<
        HTMLButtonElement,
        HTMLDivElement
    >({ contentRefs: [phonePopupRef] });

    const userContext = useUser();
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [changePasswordModelOpen, setChangePasswordModalOpen] = useState(false);
    const [phoneNumberToConfirm, setPhoneNumberToConfirm] = useState<string>();

    const onSettingsClick = () => {
        close();
        setProfileModalOpen(true);
    };

    const onChangePasswordClick = () => {
        close();
        setChangePasswordModalOpen(true);
    };

    const onPhoneNumberSave = async (
        phoneNumber: string | undefined,
        confirmationCode: string | undefined
    ) => {
        if (!userContext.user?.Id) {
            throw new Error('Attempted to save without a user id');
        }

        if (phoneNumber && !confirmationCode) {
            throw new Error('Attempted to save phone number without confirmation code');
        }

        try {
            await updateCurrentUser({
                PhoneNumber: phoneNumber === '' ? undefined : phoneNumber,
                PhoneConfirmationCode: confirmationCode,
            });
            queryClient.invalidateQueries({
                queryKey: [currentUserInfoQueryKey],
            });
            return undefined;
        } catch {
            return `Unable to save phone number.${
                confirmationCode
                    ? ' Verify the confirmation code is correct and try again.'
                    : ''
            }`;
        }
    };

    const onPhoneNumberConfirm = async (phoneNumber: string | undefined) => {
        // if attempting to clear the phone number, no need to get confirmation code
        if (!phoneNumber) {
            const result = await onPhoneNumberSave(undefined, undefined);
            return result;
        }

        // if phoneNumber is identical to number on file, we do not need to run confirmation
        if (phoneNumber === userContext.user?.PhoneNumber) {
            return undefined;
        }

        // otherwise send the confirmation text and show the confirm code modal
        try {
            await sdClient.sendConfirmationCode({
                body: new UserPhoneNumberConfirmRequest({ PhoneNumber: phoneNumber }),
            });
            setPhoneNumberToConfirm(phoneNumber);
            return undefined;
        } catch {
            return 'Unable to send confirmation code';
        }
    };

    const onPhoneNumberConfirmed = async (confirmationCode: string) => {
        const error = await onPhoneNumberSave(phoneNumberToConfirm, confirmationCode);

        if (!error) {
            setPhoneNumberToConfirm(undefined);
            toggleOpen();
        }

        return error;
    };

    return (
        <>
            <MenuButton
                aria-label="Open User Menu"
                onClick={toggleOpen}
                $active={isOpen}
                ref={triggerRef}
            >
                <FontAwesomeIcon icon={faUser} />
            </MenuButton>
            {isOpen && (
                <StatelessPopup
                    noPadding
                    popupClassName="user-menu-popup"
                    onClose={close}
                    ref={popupRef}
                    anchor={triggerRef.current}
                    ariaLabel="User menu"
                    noOverlay
                >
                    <Menu>
                        <UserMenu
                            userContext={userContext}
                            onSettingsClick={onSettingsClick}
                            onChangePasswordClick={onChangePasswordClick}
                            onPhoneNumberConfirm={onPhoneNumberConfirm}
                            phonePopupRef={phonePopupRef}
                        />
                    </Menu>
                </StatelessPopup>
            )}
            {profileModalOpen && userContext.user?.Id && (
                <UserProfileModal
                    userId={userContext.user.Id}
                    profile={userContext.user.Profile}
                    onClose={() => setProfileModalOpen(false)}
                />
            )}
            {phoneNumberToConfirm && (
                <PhoneNumberConfirmModal
                    phoneNumber={phoneNumberToConfirm}
                    onConfirm={onPhoneNumberConfirmed}
                    onCancel={() => setPhoneNumberToConfirm(undefined)}
                />
            )}
            {changePasswordModelOpen && userContext.user?.Id && (
                <ChangePasswordPopupModal
                    userContext={userContext}
                    onClose={() => setChangePasswordModalOpen(false)}
                />
            )}
        </>
    );
}
